import depsloader from "../../../depsloader";

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
  }

  cancelAutocomplete() {
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state) {
      p.push(g.state);
    }

    if (g.country) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  _mapsKey() {
    try {
      if (typeof document !== "undefined" && document.documentElement && document.documentElement.dataset) {
        return document.documentElement.dataset.swMapsKey || "";
      }
    } catch (e) {
      // noop
    }

    return "";
  }

  _geoaddress(l) {
    const geoaddress = {};
    const p = (l && l.properties) ? l.properties : {};

    const exists = (arr, obj) => {
      for (let i = 0; i < arr.length; i++) {
        if (!(arr[i] in obj)) {
          return false;
        }
        if (!obj[arr[i]]) {
          return false;
        }
      }

      return true;
    };

    const type = ("type" in p && p.type) ? p.type : null;

    // Bezirk vorrangig aus properties.type bestimmen, sonst Legacy-osm-Felder.
    let isDistrict = false;
    if (type) {
      isDistrict = (type === "district");
    } else {
      if (p.osm_key == "highway") {
        p.street = p.name;
        p.name = null;
      }

      if (p.osm_key == "boundary") {
        isDistrict = true;
      } else if (p.osm_key == "place" && p.osm_value == "suburb") {
        isDistrict = true;
      }
    }

    // Straße und Hausnummer (Treffertypen house/street sowie Legacy).
    if (exists(["street", "housenumber"], p)) {
      geoaddress["street"] = p.street;
      geoaddress["number"] = p.housenumber;
    } else if (exists(["street"], p)) {
      geoaddress["street"] = p.street;
    } else if (type === "street" && exists(["name"], p)) {
      geoaddress["street"] = p.name;
    }

    // Postleitzahl.
    if (exists(["postcode"], p)) {
      geoaddress["zip"] = p.postcode;
    }

    // Ort und Bezirk.
    if (exists(["city"], p)) {
      geoaddress["city"] = p.city;

      if (exists(["district"], p)) {
        geoaddress["district"] = p.district;
      } else if (exists(["name"], p) && p.name !== p.city && isDistrict) {
        geoaddress["district"] = p.name;
      }
    } else if (isDistrict) {
      if (exists(["district"], p)) {
        geoaddress["district"] = p.district;
      }

      if (exists(["name"], p)) {
        geoaddress["city"] = p.name;
      }
    } else if (exists(["name"], p) && type !== "street") {
      // Feature-Namen als Beschriftung erhalten, ohne ihn als Stadt zu erfinden.
      geoaddress["city"] = p.name;
    }

    if (l && l.geometry && Array.isArray(l.geometry.coordinates)) {
      geoaddress["lat"] = l.geometry.coordinates[1];
      geoaddress["lng"] = l.geometry.coordinates[0];
    }

    if (exists(["state"], p)) {
      geoaddress["state"] = p.state;
    }

    if (exists(["country"], p)) {
      geoaddress["country"] = p.country;
    }

    geoaddress["text"] = this._geoaddressText(geoaddress);

    return geoaddress;
  }

  geocode(params) {
    return new Promise((resolve, reject) => {
      this.axios.get("https://api.screenwork.de/geocoder/search", {
        cancelToken: this.axiosRequest ? this.axiosRequest.source.token : undefined,
        timeout: 25000,
        params
      }).then(res => {
        const results = [];

        if (res.data.features) {
          res.data.features.forEach(l => {
            const geoaddress = this._geoaddress(l);
            if (geoaddress) {
              results.push(geoaddress);
            }
          });
        }

        resolve(results);
      }).catch((thrown) => {
        if (!this.axios.isCancel(thrown)) {
          console.log(thrown);
          reject(thrown);
        }
      }).finally(() => {
        this.axiosRequest = null;
      });
    });
  }

  autocomplete(q) {
    const key = this._mapsKey();
    if (!key) {
      return Promise.resolve([]);
    }

    const CancelToken = this.axios.CancelToken;
    this.axiosRequest = {
      source: CancelToken.source()
    };

    const params = new URLSearchParams();
    params.append("q", q);
    params.append("lang", "de");
    params.append("limit", 10);
    params.append("osm_tag", "!place:state");
    params.append("osm_tag", "!place:house");
    //params.append("osm_tag", "!boundary:protected_area");
    params.append("osm_tag", "place");
    params.append("osm_tag", "boundary");
    params.append("key", key);

    return this.geocode(params);
  }

  reverse(lat, lng) {
    const key = this._mapsKey();

    return new Promise((resolve, reject) => {
      if (!key) {
        reject(new Error("missing maps key"));
        return;
      }

      this.axios.get("https://api.screenwork.de/geocoder/reverse", {
        timeout: 25000,
        params: {
          lat: lat,
          lon: lng,
          lang: "de",
          key: key
        }
      }).then(res => {
        if (res.data && res.data.features && res.data.features.length > 0) {
          const geoaddress = this._geoaddress(res.data.features[0]);
          resolve(geoaddress);
          return;
        }

        reject(new Error("invalid data"));
      }).catch(err => {
        reject(err);
      });
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
